import React, { FormEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { Input, Button, Modal } from '../../../../../components';
import { useAppDispatch, useAppSelector } from '../../../../../redux/Store';
import {
  addOwnedAgentEndpoint,
  AgentEndpointMethod,
} from '../../../../../redux/thunks/agentThunks';
import {
  selectEndpointSaveStatus,
  selectEndpointSaveRejection,
} from '../../../../../redux/selectors/agentSelectors';
import {
  formatEndpointPrice,
  parseAmountToSmallest,
  PAYMENT_ASSETS,
} from '../../../../../utils/formatEndpointPrice';

interface AddEndpointModalProps {
  isOpen: boolean;
  agentUserId: string;
  onClose: () => void;
}

const METHODS: AgentEndpointMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * Add-endpoint composer as a modal (extracted from the edit page
 * inline form). Own state, own submit handler. Closes itself on
 * success + toasts. Duplicate URL surfaces inline via the URL input's
 * error prop; server-side pricing rejections toast (field-level asset
 * error slot can come later if needed).
 *
 * Keeps the whole-unit price input UX the inline composer had
 * (blank fields = Free, live "Preview: X per call" hint).
 */
export const AddEndpointModal: React.FC<AddEndpointModalProps> = ({
  isOpen,
  agentUserId,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const endpointSaveStatus = useAppSelector(selectEndpointSaveStatus);
  const endpointSaveRejection = useAppSelector(selectEndpointSaveRejection);

  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [method, setMethod] = useState<AgentEndpointMethod>('POST');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [priceAsset, setPriceAsset] = useState('USDC');
  const [priceError, setPriceError] = useState<string | null>(null);

  const reset = () => {
    setLabel('');
    setUrl('');
    setDescription('');
    setMethod('POST');
    setPriceMin('');
    setPriceMax('');
    setPriceError(null);
    // Asset intentionally NOT reset — most owners adding two
    // endpoints in a row will pay in the same asset.
  };

  const handleClose = () => {
    // Full reset on close so a re-open starts clean (matches
    // "modal-triggered form" convention). Inline composer used to
    // keep unsaved input around; modal shouldn't.
    reset();
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;

    const minSmallest = priceMin.trim() === ''
      ? '0'
      : parseAmountToSmallest(priceMin, priceAsset);
    const maxSmallest = priceMax.trim() === ''
      ? '0'
      : parseAmountToSmallest(priceMax, priceAsset);
    if (minSmallest === null || maxSmallest === null) {
      setPriceError(`Enter a valid ${priceAsset} amount (e.g. 0.5).`);
      return;
    }
    if (BigInt(maxSmallest) < BigInt(minSmallest)) {
      setPriceError('Max price must be at least the min price.');
      return;
    }
    setPriceError(null);

    const result = await dispatch(
      addOwnedAgentEndpoint({
        agentUserId,
        endpoint: {
          label: label.trim() || null,
          url: url.trim(),
          description: description.trim() || null,
          method,
          priceMinSmallest: minSmallest,
          priceMaxSmallest: maxSmallest,
          priceAsset,
        },
      }),
    );

    if (result.meta.requestStatus === 'fulfilled') {
      toast.success('Endpoint added');
      handleClose();
    } else {
      const payload = result.payload as
        | { kind: string; message: string }
        | undefined;
      // Duplicate URL surfaces inline via endpointSaveRejection on
      // the URL input; other errors toast.
      if (payload && payload.kind !== 'duplicate') {
        toast.error(payload.message || 'Failed to add endpoint');
      }
    }
  };

  const isSaving = endpointSaveStatus === 'saving';

  return (
    <Modal
      isOpen={isOpen}
      title="Add endpoint"
      onClose={handleClose}
      size="md"
      // Adds the `.edit-agent` scope so EditAgent.scss rules like
      // .edit-agent__composer-row, .edit-agent__price-row, and the
      // input picker styling actually apply inside the modal
      // (those rules are all descendant-scoped under `.edit-agent`
      // and the modal renders outside that scope by default).
      className="edit-agent"
    >
      <form onSubmit={handleSubmit} className="edit-agent__form" noValidate>
        <div className="edit-agent__composer-row">
          <label className="input-wrapper edit-agent__method-picker">
            <span className="input-label">Method</span>
            <select
              className="input-field"
              value={method}
              onChange={(e) => setMethod(e.target.value as AgentEndpointMethod)}
              disabled={isSaving}
            >
              {METHODS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
          <Input
            label="Label (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={60}
            placeholder="translate"
            disabled={isSaving}
          />
        </div>
        <Input
          label="URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          type="url"
          placeholder="https://example.com/api/translate"
          required
          disabled={isSaving}
          error={
            endpointSaveRejection?.kind === 'duplicate'
              ? endpointSaveRejection.message
              : undefined
          }
        />
        <label className="input-wrapper full-width">
          <span className="input-label">Description (optional)</span>
          <textarea
            className="input-field edit-agent__textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="What does this endpoint do?"
            disabled={isSaving}
          />
        </label>

        {/* Pricing — blank fields = Free. Min = Max = fixed. Range
            otherwise. Server rejects non-Free pricing without a
            matching wallet. */}
        <div className="edit-agent__price-row">
          <Input
            label="Min price (optional)"
            value={priceMin}
            onChange={(e) => setPriceMin(e.target.value)}
            placeholder="0"
            disabled={isSaving}
          />
          <Input
            label="Max price (optional)"
            value={priceMax}
            onChange={(e) => setPriceMax(e.target.value)}
            placeholder="0"
            disabled={isSaving}
          />
          <label className="input-wrapper edit-agent__method-picker">
            <span className="input-label">Asset</span>
            <select
              className="input-field"
              value={priceAsset}
              onChange={(e) => setPriceAsset(e.target.value)}
              disabled={isSaving}
            >
              {PAYMENT_ASSETS.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </label>
        </div>

        {priceError && (
          <div className="edit-agent__error" role="alert">{priceError}</div>
        )}
        {(priceMin.trim() !== '' || priceMax.trim() !== '') && (
          <p className="edit-agent__hint">
            Preview:{' '}
            <strong>
              {formatEndpointPrice({
                priceMinSmallest:
                  parseAmountToSmallest(priceMin || '0', priceAsset) ?? '0',
                priceMaxSmallest:
                  parseAmountToSmallest(priceMax || '0', priceAsset) ?? '0',
                priceAsset,
              })}
            </strong>{' '}
            per call
          </p>
        )}

        <div className="edit-agent__actions">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSaving || !url.trim()}
          >
            {isSaving ? 'Adding…' : 'Add endpoint'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddEndpointModal;
